/**
 * AdvanceAgreementArea — "Is the Schedule executed by both parties?"
 *
 * THE PARTIES COME FIRST BECAUSE THEY ARE WHAT THE DOCUMENT CANNOT BE GENERATED WITHOUT. Five of
 * loan v7's eighteen merge keys are the Borrower's name, address and phone and the Vehicle's model
 * and registration, and NONE of them is edited on this dossier: they are facts about the contract,
 * fixed on the contract's own driver and vehicle areas. So the block states them and links out,
 * exactly as `AgreementsArea`'s parties block does — the last chance anybody has to notice that the
 * wrong person or the wrong car has been named is the moment before the Schedule is minted over
 * them.
 *
 * THE LENDER IS A CONSTANT AND IS STILL WORTH DRAWING. The Schedule names three parties and an
 * admin proof-reading it against this block should find three; a row missing because its value
 * never varies reads as a party who is missing.
 *
 * SENDING IS WHAT GENERATES IT [F0], AND NOTHING ON THE BOARD BLOCKS THE SEND. `agreement_create`
 * is called by the row's own button whenever no document exists, and `missing[]` is only fetched
 * once one does — so the pre-flight below the row is ADVISORY and the lede says so. An unresolved
 * key prints literally on a document a driver can be handed and can sign, which is why the rail
 * carries the hire purchase reference as a row of its own.
 *
 * THE D-B55 LOCK IS THE BOARD'S OWN, NOT A SECOND READING OF IT. `advanceLockedFor` hangs
 * `ADVANCE_LOCK_REASON` on this slot while the driver has not signed the hire purchase agreement —
 * a paper `contract_agreements` row satisfying it as fully as a signed seat does — and this line is
 * drawn by comparing against that exported constant, so the padlock on the row and the sentence
 * above it can never describe two different rules.
 *
 * A PAPER ADVANCE IS A READ AND PERMANENTLY SO [F3]. `agreement_create` refuses every loan whose
 * `loan_agreements` row is signed with `COALESCE(origin,'SCANNED') = 'SCANNED'` — all 161 of the
 * production advances — so the row draws the paper original and its scan, and no ceremony at all.
 */

import AgreementSlotList, {
    type ContractAgreementsModel,
} from '@/components/agreements/AgreementSlotList';
import { ADVANCE_LOCK_REASON, type BoardSlot } from '@/components/agreements/boardSlots';

import { Block, Blocks, Hint, LinkLike, LockLine, Rec, Rows } from '../../areas/blocks';
import type { SetupArea } from '../../steps';

interface Props {
    /** The route's subject: an existing advance, or the create flow. */
    loanId: number | 'new';
    /** The PAGE's signing model, at `junctionScope: 'all'`. */
    agreements: ContractAgreementsModel;
    /** `loanSlotsForLoan(agreements.slots, loanId)` — this advance's row, and only this one. */
    slots: BoardSlot[];
    driver: { fullName: string | null; phone: string | null; address: string | null };
    vehicle: { make: string | null; model: string | null; registration: string | null };
    /** The number this Schedule quotes in three places — live document, else [D-B52]'s paper one. */
    hpReference: string | null;
    /** The driver has signed the hire purchase agreement — the D-B55 gate. */
    hpDriverSigned: boolean;
    /** Where a merge-field pre-flight or a locked slot sends the admin. TOTAL over the vocabulary. */
    onDeepLink: (area: string) => void;
    /** Out to the contract's own setup canvas, where the parties are edited. */
    onGoToSetupArea: (area: SetupArea) => void;
}

export default function AdvanceAgreementArea({
    loanId,
    agreements,
    slots,
    driver,
    vehicle,
    hpReference,
    hpDriverSigned,
    onDeepLink,
    onGoToSetupArea,
}: Props) {
    const isNew = loanId === 'new';
    const slot = slots[0] ?? null;
    const document = slot?.document ?? null;
    const paper = !document && !!slot?.legacySignedAt;
    /**
     * THE SLOT'S OWN REASON, MATCHED RATHER THAN RE-DERIVED — `AgreementsArea`'s rule for the
     * partner lock, applied to this one.
     *
     * `advanceLockedFor` LIFTS FOR AN ADVANCE SOMEBODY HAS ALREADY SIGNED, whatever the hire
     * purchase agreement is doing: voiding and reissuing the master takes the driver's signature
     * away again, and re-locking this row would hide a signature that has already been given behind
     * a padlock. A second reading of `driverSigned` here would miss that and contradict the padlock
     * on the row underneath. With no slot at all there is no document and no legacy row, so the
     * predicate reduces to the driver's arm alone — which is what this says.
     */
    const locked = slot ? slot.blockedReason === ADVANCE_LOCK_REASON : !isNew && !hpDriverSigned;

    const vehicleName = [vehicle.make, vehicle.model].filter(Boolean).join(' ');

    /**
     * THE THREE-WAY LEDE — no advance yet, an advance with no document, a document out.
     *
     * They are three genuinely different states and not three phrasings of one: before the advance
     * exists there is no subject to generate over, after it exists the send is the first move, and
     * once the document is out the only thing left to say is who is holding it.
     */
    const lede = isNew
        ? 'The Schedule is generated once the advance is saved. Nothing is sent from here until then — there is no advance for it to be about.'
        : paper
          ? 'This advance was papered outside the app, and that record is permanent: the server refuses to raise an in-app Schedule over an advance already signed on paper.'
          : !document
            ? 'Sending IS what generates it: the borrower signs it in the Primelane driver app, and Primelane countersigns once the hire purchase and witness agreements are signed. The pre-flight below names any wording the Schedule still cannot resolve — it does not hold the send, so read it first.'
            : 'The borrower signs this in the Primelane driver app. Primelane countersigns once the hire purchase and witness agreements are signed, and the executed PDF is filed automatically after that.';

    return (
        <Blocks>
            <Block id="b-parties" title="Parties">
                <p className="sd-lede sd-dim">
                    The Schedule names three: the Borrower who takes the advance, Primelane who
                    lends it, and the vehicle the hire purchase agreement it sits under is written
                    over. None of them is edited here — they are facts about the contract.
                </p>

                <Rows>
                    <Rec
                        label="1 · Borrower"
                        value={driver.fullName}
                        sub={
                            <>
                                {[driver.phone, driver.address].filter(Boolean).join(' · ') ||
                                    'phone and address missing'}
                                {' · signs in the driver app · '}
                                <LinkLike onClick={() => onGoToSetupArea('driver')}>
                                    Edit in Driver
                                </LinkLike>
                            </>
                        }
                        empty="No driver assigned"
                    />
                    <Rec
                        label="2 · Lender"
                        value="Primelane Ghana Ltd"
                        sub="countersigns once the driver has signed the hire purchase agreement and the witness the witness agreement"
                    />
                    <Rec
                        label="Vehicle"
                        value={vehicleName || null}
                        sub={
                            <>
                                {vehicle.registration || 'registration missing'}
                                {' · '}
                                <LinkLike onClick={() => onGoToSetupArea('vehicle')}>
                                    Edit in Vehicle
                                </LinkLike>
                            </>
                        }
                        empty="No vehicle on this contract"
                    />
                    {/* THE REFERENCE IS THE SERVER'S ANSWER, NOT A LOOKUP OF OUR OWN [F1, D-B52]. The
                        Schedule quotes the hire purchase agreement's number in three places, and a
                        contract whose agreement was signed on paper resolves the designation
                        `P-<contract id>` — permanent text, because a paper-HP contract can never be
                        given an in-app hire purchase document. */}
                    <Rec
                        label="Hire purchase reference"
                        value={hpReference}
                        sub="quoted three times on the Schedule"
                        empty="Not resolved yet"
                    />
                </Rows>

                {!hpReference && (
                    <Hint className="mt-3">
                        This contract’s hire purchase agreement has no number yet, so the Schedule
                        would print the merge field literally. Generate or record the hire purchase
                        agreement on the contract’s own Agreements area first —{' '}
                        <LinkLike onClick={() => onGoToSetupArea('agreements')}>
                            Open Agreements
                        </LinkLike>
                        .
                    </Hint>
                )}
            </Block>

            <Block id="b-advanceagr" title="Operational expense advance" aside="borrower instrument">
                {locked && (
                    <LockLine title="Locked until the driver signs the hire purchase agreement">
                        {ADVANCE_LOCK_REASON} An advance is a second bargain with credit exposure of
                        its own, so the borrower may sign it only once the agreement it sits under
                        has been signed — a paper original already on file unlocks this in
                        exactly the same way, because the signature is what matters and not the
                        document.
                    </LockLine>
                )}

                <p className="sd-lede sd-dim">{lede}</p>

                {isNew ? (
                    <Rows>
                        <Rec
                            label="Operational Expense Advance"
                            value={null}
                            empty="Generated once the advance is saved"
                        />
                    </Rows>
                ) : slots.length > 0 ? (
                    <AgreementSlotList
                        slots={slots}
                        agreements={agreements}
                        onDeepLink={onDeepLink}
                    />
                ) : (
                    /* THREE ROUND TRIPS STAND BEHIND THIS BOARD, and an unread one is not an
                       absent instrument: `buildBoardSlots` returns nothing until the contract row
                       and the subjects have both landed. The sentence says which of the two it is
                       rather than announcing that a Schedule nobody can see yet does not exist. */
                    <p className="sd-hint">
                        {agreements.loading
                            ? 'Reading the signing board…'
                            : agreements.failed
                              ? 'The signing board could not be read, so nothing can be said about this advance’s Schedule yet.'
                              : 'This advance has no row on the signing board yet.'}
                    </p>
                )}
            </Block>
        </Blocks>
    );
}
